import { Guid } from "guid-typescript";
import _ from "lodash";
import React, { PureComponent, ReactNode } from "react";
import Slider, { Settings } from "react-slick";
import DynamicStyleManager from "../../../../../shared/utilty/dynamic-style-manager";
import "./carousel.scss";

export interface CarouselBodyWrapperProps extends Settings {
  children?: ReactNode;
  style?: any;
  setCurrentSlideNumber?: (val: number) => void;
  onChange?: () => void;
  startingIndex?: number;
  hoverBgColor?: string;
  hoverFontColor?: string;
  arrowColor?: string;
}

interface CarouselBodyWrapperState {
  mouseOver: boolean;
  width: number;
  height: number;
}

declare let window: Window & { kuika: any };

export class CarouselBodyWrapper extends PureComponent<CarouselBodyWrapperProps, CarouselBodyWrapperState> {
  private uniqueKey = Guid.create();

  private style = new DynamicStyleManager(`kcarouselbodywrapper_${this.uniqueKey}`);

  private sliderRef;

  private resizeObserver: ResizeObserver | null = null;

  private containerRef: React.RefObject<HTMLDivElement>;

  constructor(props: CarouselBodyWrapperProps) {
    super(props);
    this.sliderRef = React.createRef();
    this.containerRef = React.createRef();
    this.state = {
      mouseOver: false,
      width: 0,
      height: 0
    };
  }

  componentDidUpdate(prevState: any) {
    if (prevState.mouseOver !== this.state.mouseOver) this.setDynamicStyle();
  }

  componentDidMount = () => {
    if (this.props.startingIndex && this.props.startingIndex > 0) {
      this.sliderRef.current.slickGoTo(this.props.startingIndex, true);
    }
    const carousel: HTMLCollectionOf<Element> = document?.getElementsByClassName("kuika-carousel__vertical");
    const parent = carousel[0]?.parentElement;
    if (parent && parent.style) {
      parent.style.paddingBottom = "0px";
      parent.style.paddingRight = "25px";
    }
    this.setDynamicStyle();
    this.setupResizeObserver();
  };

  componentWillUnmount() {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
    this.style.removeStyle();
  }

  setupResizeObserver = () => {
    if (this.containerRef.current) {
      const parent = this.containerRef.current.parentElement;
      const isInTd = this.containerRef.current.closest("td");

      if (parent && isInTd) {
        this.resizeObserver = new ResizeObserver((entries) => {
          for (const entry of entries) {
            const width = entry.contentRect.width;
            const height = entry.contentRect.height;
            if (width !== this.state.width || height !== this.state.height) {
              this.setState({ width, height });
            }
          }
        });
        this.resizeObserver.observe(parent);
        this.setState({ width: parent.offsetWidth, height: parent.offsetHeight });
      }
    }
  };

  getDynamicCss = (): string => {
    const className: string = this.getClassName();
    if (!className || className.length === 0) {
      return "";
    }
    let result = "";
    const { arrowColor, vertical } = this.props;

    if (arrowColor) {
      result += `.${className.trim()} .slick-prev:before, .${className.trim()} .slick-next:before {
          color: ${arrowColor} !important;
        }`;
    }

    result += `.${className.trim()} {
      display: block;
      position: relative;
      box-sizing: border-box;
    }`;

    return result;
  };

  setDynamicStyle = () => {
    this.style.setDynamicCss(this.getDynamicCss());
  };

  getClassName = () => {
    let result = "";
    if (!this.uniqueKey) {
      return result;
    }
    result = `kcarousel_${this.uniqueKey.toString().substring(0, 8)}`;
    return result;
  };

  getProps = () => {
    const props: any = _.clone(this.props);

    if (props.slidesToShow && props.slidesToShow > 0) {
      if (props.children && props.children[1] && props.children[1].length >= 0) {
        if (props.children[1].length < props.slidesToShow) {
          props.slidesToShow = props.children[1].length;
        }
      }
    }
    if (!this.props.children || this.props.children[1].length === undefined) props.slidesToShow = 1;

    if (props.vertical) {
      props.className += " kuika-carousel__vertical";
      props.dots = true;
    } else {
      props.className += " kuika-carousel__horizontal";
    }

    if (props.vertical) {
      props.variableWidth = false;
      props.adaptiveHeight = false;
    } else {
      props.variableWidth = false;
      props.adaptiveHeight = true;
    }

    return props;
  };

  handleMouseOver = () => {
    const className = this.getClassName();

    if (!className) {
      return;
    }

    const currentCarousel = document.querySelector(`.${className}`) as HTMLElement | null;

    if (currentCarousel) {
      if (!currentCarousel.hasAttribute("data-original-bg")) {
        currentCarousel.setAttribute("data-original-bg", currentCarousel.style.backgroundColor || "");
      }
      if (!currentCarousel.hasAttribute("data-original-color")) {
        currentCarousel.setAttribute("data-original-color", currentCarousel.style.color || "");
      }
      if (!currentCarousel.hasAttribute("data-original-transition")) {
        currentCarousel.setAttribute("data-original-transition", currentCarousel.style.transition || "");
      }

      currentCarousel.style.transition = "background-color 0.3s ease, color 0.3s ease";

      if (this.props.hoverBgColor) {
        currentCarousel.style.backgroundColor = this.props.hoverBgColor;
      }

      const labels = currentCarousel.querySelectorAll("label, span");
      labels.forEach((element) => {
        const el = element as HTMLElement;
        if (!el.hasAttribute("data-original-color")) {
          el.setAttribute("data-original-color", el.style.color || "");
        }
        if (!el.hasAttribute("data-original-transition")) {
          el.setAttribute("data-original-transition", el.style.transition || "");
        }

        el.style.transition = "color 0.3s ease";

        if (this.props.hoverFontColor) {
          el.style.color = this.props.hoverFontColor;
        }
      });
    }

    this.setState({
      mouseOver: true
    });
  };

  handleMouseOut = () => {
    const className = this.getClassName();
    if (!className) {
      return;
    }

    const currentCarousel = document.querySelector(`.${className}`) as HTMLElement | null;

    if (currentCarousel) {
      currentCarousel.style.backgroundColor = currentCarousel.getAttribute("data-original-bg") || "";
      currentCarousel.style.color = currentCarousel.getAttribute("data-original-color") || "";

      setTimeout(() => {
        if (currentCarousel) {
          currentCarousel.style.transition = currentCarousel.getAttribute("data-original-transition") || "";
          currentCarousel.removeAttribute("data-original-bg");
          currentCarousel.removeAttribute("data-original-color");
          currentCarousel.removeAttribute("data-original-transition");
        }
      }, 300);

      const labels = currentCarousel.querySelectorAll("label, span");
      labels.forEach((element) => {
        const el = element as HTMLElement;
        el.style.color = el.getAttribute("data-original-color") || "";

        setTimeout(() => {
          if (el) {
            el.style.transition = el.getAttribute("data-original-transition") || "";
            el.removeAttribute("data-original-color");
            el.removeAttribute("data-original-transition");
          }
        }, 300);
      });
    }

    this.setState({
      mouseOver: false
    });
  };

  getPaddingBottom = () => {
    if (this.props.style && this.props.style?.paddingBottom) {
      return 25 + Number.parseInt(this.props.style?.paddingBottom.toString());
    }
    return 25;
  };

  handleAfterChange = (nextSlide: number) => {
    if (this.props.setCurrentSlideNumber) {
      this.props.setCurrentSlideNumber(nextSlide);
    }
    if (this.props.onChange) {
      this.props.onChange();
    }
  };

  getStyle = () => {
    let result: any = {
      ...this.props.style,
      paddingBottom: this.getPaddingBottom(),
      display: "block",
      position: "relative",
      boxSizing: "border-box",
      width: this.state.width || "auto",
      transition: "background-color 0.3s ease, color 0.3s ease"
    };

    if (this.props.vertical && this.state.height > 0) {
      result.height = this.state.height;
    }

    return result;
  };

  render(): ReactNode {
    return (
      <>
        <div
          ref={this.containerRef}
          className={this.getClassName()}
          style={this.getStyle()}
          onMouseOver={this.handleMouseOver}
          onMouseOut={this.handleMouseOut}
        >
          <Slider ref={this.sliderRef} {...this.getProps()} afterChange={this.handleAfterChange} />
        </div>
      </>
    );
  }
}
